import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { CircleDollarSign, Fuel, Package, Wrench } from 'lucide-react';
import { useId, useMemo, useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Campo } from '@/componentes/Campo';
import { CardIndicador } from '@/componentes/CardIndicador';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { Medida } from '@/componentes/Medida';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { formatarDecimal, formatarReais, formatarReaisCompacto } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { variacaoDoPeriodo } from '@/lib/variacao';
import { OPCOES_POR_PAGINA, PADRAO_POR_PAGINA, contagem, fatiarPagina } from '@/modulos/painel/painel';
import { useSessao } from '@/modulos/sessao/consultas';
import { montarBuscaDePeriodo, periodoDaBusca } from '@/lib/busca-de-periodo';

import { api, ROTULO_DO_CRITERIO, type Criterio, type CustoDoEquipamento } from './api';
import { CustoIndireto } from './CustoIndireto';
import { estadoDaFaixa, oQueOTotalNaoInclui, totaisDoPeriodo } from './custos';
import { DepreciacaoDasMaquinas } from './DepreciacaoDasMaquinas';
import { ValorHoraDasFuncoes } from './ValorHoraDasFuncoes';

/**
 * Custos — módulo 11 do v6, ferramentas 1, 2 e 4: o custo direto por máquina,
 * sem contar duas vezes o mesmo fato, e o custo por hora com o denominador
 * escrito.
 *
 * O critério de custeio do combustível é a decisão 3 do dono — parâmetro da
 * empresa, e não constante no código. Ele aparece em cima, com quem pode
 * trocá-lo.
 *
 * Desde a 0096 a tela mostra o custo DIRETO e, ao lado, o custo TOTAL — o
 * direto mais a depreciação linear e o custo indireto rateado —, que é o que a
 * margem usa. O total só se diz completo quando a lista de ausências do
 * servidor está vazia; senão a tela escreve cada uma (o v6: "chamar custo
 * parcial de custo total ou margem final" é o risco). Abaixo, os três
 * parâmetros que mudam o custo de todo o histórico: o critério do combustível,
 * o valor-hora das funções, o custo indireto com o critério do rateio, e a
 * depreciação de cada máquina.
 */

/* O cálculo do período padrão saiu daqui para `src/lib/busca-de-periodo.ts`,
   compartilhado com a Visão do gestor — e com ele o período foi para a URL
   (p.16): "o estado dos filtros vai para a URL, para o usuário salvar e
   compartilhar a visão". Em `useState`, recarregar voltava sempre aos últimos
   30 dias, e mandar o link de um custo mandava outro custo. */
const rota = getRouteApi('/custos');

/**
 * A variacao de um cartao de custo. Os quatro desta tela sao somas DENTRO do
 * periodo, e nos quatro subir e piorar — por isso o sentido nao e parametro
 * aqui: repeti-lo quatro vezes convidaria a errar um.
 */
function variacaoContra(atual: number, anterior: number | undefined) {
  return variacaoDoPeriodo(atual, anterior, 'subir_e_pior') ?? {};
}

export function PaginaCustos() {
  const base = useId();
  const sessao = useSessao();
  const podeConfigurar = (sessao.data?.permissoes ?? []).includes('custo.configurar');
  const podeGravarValorHora = (sessao.data?.permissoes ?? []).includes('valor_hora.gravar');
  const podeGravarDepreciacao = (sessao.data?.permissoes ?? []).includes('depreciacao.gravar');
  const podeLancarIndireto = (sessao.data?.permissoes ?? []).includes('custo_indireto.lancar');
  const podeEstornarIndireto = (sessao.data?.permissoes ?? []).includes('custo_indireto.estornar');
  /* O atalho para cadastrar máquina só para quem cadastra (`equipamento.gerir`). */
  const podeCadastrarMaquina = (sessao.data?.permissoes ?? []).includes('equipamento.gerir');
  const busca = rota.useSearch();
  const navegar = rota.useNavigate();
  const { de, ate } = useMemo(() => periodoDaBusca(busca), [busca]);

  /* Trocar o período é NAVEGAR: assim o botão de voltar do navegador desfaz a
     troca, que é o que a pessoa espera dele. */
  const irPara = (novo: { readonly de: string; readonly ate: string }) => {
    void navegar({ search: montarBuscaDePeriodo(novo) });
  };
  const setDe = (valor: string) => irPara({ de: valor, ate });
  const setAte = (valor: string) => irPara({ de, ate: valor });

  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(PADRAO_POR_PAGINA);

  const custos = useQuery({ queryKey: ['custos', de, ate], queryFn: () => api.equipamentos(de, ate) });
  const linhas = useMemo(() => custos.data?.equipamentos ?? [], [custos.data]);
  const totais = useMemo(() => totaisDoPeriodo(linhas), [linhas]);
  const anterior = custos.data?.anterior;
  const faixa = estadoDaFaixa(custos);
  const fatia = fatiarPagina(linhas, pagina, porPagina);

  /* Linha de altura fixa, 48 px com duas informações (p.18): código em cima;
     embaixo a descrição (uma linha, com o texto inteiro no `title`) e o que
     ficou FORA DA CONTA daquela máquina. O "fora da conta" era uma oitava
     coluna de cabeçalho largo e quase sempre vazia ("—"); aqui ele fica junto
     da máquina a que se refere, e a tabela cabe no quadro de 1280 px.

     Peças e lubrificante e Terceiros são SECUNDÁRIAS (R01, p.28): saem em M
     (1024–1279) e com o quadro estreito. Os totais das duas estão na faixa de
     indicadores logo acima. */
  const reais = (v: number) => <span className="numero whitespace-nowrap text-tinta">{formatarReais(v)}</span>;
  const foraDaConta = (e: CustoDoEquipamento) =>
    [
      e.semPreco > 0 ? `${String(e.semPreco)} sem preço` : null,
      /* A hora de pessoa fora do total (0095), curta na linha; a frase inteira
         do servidor está abaixo da faixa. */
      (e.horasSemValor ?? 0) > 0 ? `${formatarDecimal(e.horasSemValor ?? 0, 1)} h sem valor` : null,
    ]
      .filter(Boolean)
      .join(' · ');
  const COLUNAS: readonly Coluna<CustoDoEquipamento>[] = [
    {
      chave: 'equipamento',
      rotulo: 'Equipamento',
      celula: (e) => {
        const fora = foraDaConta(e);
        /* Uma frase so na segunda linha: ela quebra entre palavras quando o
           quadro aperta, e nao prende a coluna na largura da nota inteira. */
        return (
          <span className="flex min-w-0 flex-col">
            <span className="codigo text-tinta">{e.codigo}</span>
            <TextoDaCelula
              texto={fora ? `${e.descricao} · ${fora}` : e.descricao}
              classe="txt-apoio text-tinta-apagada"
              linhas={1}
            />
          </span>
        );
      },
    },
    { chave: 'combustivel', rotulo: 'Combustível', historia: 'COL-CST-03', alinhamento: 'numero', celula: (e) => reais(e.combustivel), total: () => reais(totais.combustivel) },
    {
      chave: 'estoque',
      rotulo: 'Peças e lubrificante',
      alinhamento: 'numero',
      prioridade: 'secundaria',
      celula: (e) => reais(e.estoque),
      total: () => reais(totais.estoque),
    },
    {
      chave: 'terceiros',
      rotulo: 'Terceiros',
      historia: 'COL-CST-04',
      alinhamento: 'numero',
      prioridade: 'secundaria',
      celula: (e) => reais(e.terceiros),
      total: () => reais(totais.terceiros),
    },
    {
      chave: 'maoDeObra',
      rotulo: 'Mão de obra',
      alinhamento: 'numero',
      prioridade: 'secundaria',
      celula: (e) => reais(e.maoDeObra ?? 0),
      total: () => reais(totais.maoDeObra),
    },
    { chave: 'total', rotulo: 'Total direto', alinhamento: 'numero', celula: (e) => reais(e.total), total: () => reais(totais.total) },
    {
      /* 0096 · o custo TOTAL, com a depreciação e o indireto na segunda linha.
         A máquina sem dado de depreciação diz isso ali, por extenso. */
      chave: 'custoTotal',
      rotulo: 'Custo total',
      alinhamento: 'numero',
      celula: (e) => (
        <span
          className="flex flex-col items-end"
          title={`Direto ${formatarReais(e.total)} · depreciação ${e.semDepreciacao ? 'sem dado' : formatarReais(e.depreciacao ?? 0)} · indireto ${formatarReais(e.indiretoRateado ?? 0)}`}
        >
          {reais(e.custoTotal ?? e.total)}
          <span className="txt-apoio whitespace-nowrap text-tinta-apagada">
            {/* Curto (R$ 10 mil): a linha de apoio não pode alargar a coluna
                a ponto de espremer o nome da máquina; o valor exato está no `title`. */}
            {e.semDepreciacao
              ? `sem depreciação · ind. ${formatarReaisCompacto(e.indiretoRateado ?? 0)}`
              : `dep. ${formatarReaisCompacto(e.depreciacao ?? 0)} · ind. ${formatarReaisCompacto(e.indiretoRateado ?? 0)}`}
          </span>
        </span>
      ),
      total: () => reais(totais.custoTotal),
    },
    {
      /* O custo total por hora produtiva (guia 9.1), com o direto e as horas
         na segunda linha — a coluna "Hora produtiva" virou essa linha para a
         tabela caber em oito colunas (p.27). */
      chave: 'custoHora',
      rotulo: 'Custo total/h',
      historia: 'NUM-CST-02',
      alinhamento: 'numero',
      celula: (e) => (
        <span className="flex flex-col items-end">
          {/* Sem hora apontada não há divisão: "—", explicado junto do período. */}
          {(e.custoTotalPorHora ?? e.custoPorHora) === null ? (
            <span className="text-tinta-secundaria">—</span>
          ) : (
            <span className="whitespace-nowrap">
              <Medida valor={formatarReais(e.custoTotalPorHora ?? e.custoPorHora ?? 0)} unidade="/h" colado />
            </span>
          )}
          <span
            className="txt-apoio whitespace-nowrap text-tinta-apagada"
            {...(e.custoPorHora === null ? {} : { title: `Direto ${formatarReais(e.custoPorHora)}/h` })}
          >
            {`${formatarDecimal(e.horasProducao, 1)} h produtivas`}
          </span>
        </span>
      ),
    },
  ];

  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        <CabecalhoPagina
          sobretitulo="Custos"
          titulo="Custo por máquina"
          subtitulo="O direto — combustível, peças, lubrificante, terceiros e mão de obra — e o total, com a depreciação e o custo indireto rateado, por hora produtiva"
        />

        {/* Sem resposta não há total (revisão R01/R02): esqueleto enquanto
            carrega, e a falha com o que fazer. A soma de uma lista que não
            chegou dava "R$ 0,00" nos quatro cards. Carregando (p.32), os
            quatro cards já se sabem: o rótulo e o ícone ficam, e só o número
            é esqueleto. */}
        {faixa === 'falhou' ? (
          <div role="alert" className="flex flex-wrap items-center justify-between gap-4 rounded-controle border border-borda bg-rebaixada p-4">
            <span className="flex flex-col gap-1">
              <span className="txt-corpo text-tinta">Os totais do período não carregaram.</span>
              <span className="txt-apoio text-tinta-secundaria">{explicarFalha(custos.error).comoResolver}</span>
            </span>
            <Botao nivel="secundario" onClick={() => void custos.refetch()}>
              Carregar de novo
            </Botao>
          </div>
        ) : (
          <>
            {/* Contexto curto (p.17): a faixa se lê de relance. O que o total não
                inclui, por extenso, vai no texto logo abaixo dela. */}
            <FaixaIndicadores>
              {/* 0096 · o custo TOTAL primeiro: é o que a margem usa. O
                  terceiro cartão de antes (Terceiros) saiu para caber o total
                  nos quatro da faixa (p.26); a soma de terceiros segue no rodapé
                  da tabela. */}
              <CardIndicador
                rotulo="Custo total"
                icone={CircleDollarSign}
                tom="marca"
                carregando={faixa === 'carregando'}
                valor={formatarReais(totais.custoTotal)}
                contexto={
                  custos.data?.completo
                    ? 'completo · direto, depreciação e indireto'
                    : `parcial · ${contagem(custos.data?.ausencias?.length ?? 1, 'coisa ficou fora', 'coisas ficaram fora')}`
                }
                {...variacaoContra(totais.custoTotal, anterior?.custoTotal)}
              />
              <CardIndicador
                historia="NUM-CST-01"
                rotulo="Custo direto"
                icone={Wrench}
                carregando={faixa === 'carregando'}
                valor={formatarReais(totais.total)}
                contexto={
                  totais.semPreco > 0
                    ? `${contagem(totais.semPreco, 'lançamento sem preço', 'lançamentos sem preço')}`
                    : 'combustível, peças, terceiros e mão de obra'
                }
                /* A VARIACAO CONTRA O PERIODO ANTERIOR (R02, p.31): todos somam
                   DENTRO do periodo, e em todos subir e piorar. Sem base de
                   comparacao `variacaoDoPeriodo` devolve nulo e o cartao fica so
                   com o valor — "+100%" sobre base zero parece medida e e so a
                   base faltando. */
                {...variacaoContra(totais.total, anterior?.total)}
              />
              <CardIndicador rotulo="Combustível" icone={Fuel} carregando={faixa === 'carregando'} valor={formatarReais(totais.combustivel)} contexto={custos.data ? ROTULO_DO_CRITERIO[custos.data.criterio] : 'critério da empresa'} {...variacaoContra(totais.combustivel, anterior?.combustivel)} />
              <CardIndicador rotulo="Peças e lubrificante" icone={Package} carregando={faixa === 'carregando'} valor={formatarReais(totais.estoque)} contexto="ao custo médio das entradas de cada item" {...variacaoContra(totais.estoque, anterior?.estoque)} />
            </FaixaIndicadores>

            {faixa === 'carregando' ? (
              <Carregando oQue="Carregando os custos do período" className="max-w-[80ch]">
                <Barra className="my-1 h-3 w-1/2" />
              </Carregando>
            ) : (
              <p className="txt-apoio max-w-[80ch] text-pretty text-tinta-secundaria">{oQueOTotalNaoInclui(custos.data?.ausencias, custos.data?.completo)}</p>
            )}
          </>
        )}

        <section className="cartao flex flex-wrap items-end gap-4 p-6">
          <Campo id={`${base}-de`} rotulo="De">
            <input id={`${base}-de`} type="date" className="controle" value={de} onChange={(e) => { setDe(e.target.value); }} />
          </Campo>
          <Campo id={`${base}-ate`} rotulo="Até">
            <input id={`${base}-ate`} type="date" className="controle" value={ate} onChange={(e) => { setAte(e.target.value); }} />
          </Campo>
          <p className="txt-apoio pb-2 text-tinta-secundaria">
            {/* O denominador vem da resposta: até ela chegar, a frase é barra. */}
            {custos.isPending ? (
              <Barra className="my-1 h-3 w-80 max-w-full" />
            ) : custos.data ? (
              `Custo por hora dividido por ${custos.data.denominador}. Sem hora apontada, a coluna Custo total/h mostra —.`
            ) : (
              ''
            )}
          </p>
        </section>

        {custos.data ? (
          <CriterioDaEmpresa atual={custos.data.criterio} podeTrocar={podeConfigurar} />
        ) : custos.isPending ? (
          <EsqueletoDoCriterio podeTrocar={podeConfigurar} />
        ) : null}

        <Tabela
          rotulo="Custo por máquina"
          colunas={COLUNAS}
          linhas={fatia.visiveis}
          alturaDupla
          rotuloDoTotal="Soma"
          chaveDaLinha={(e) => e.equipamentoId}
          carregando={custos.isPending}
          erro={
            custos.isError ? (
              <EstadoVazio
                titulo="Não foi possível carregar os custos"
                descricao={explicarFalha(custos.error).comoResolver}
                acao={
                  <Botao nivel="secundario" onClick={() => void custos.refetch()}>
                    Carregar de novo
                  </Botao>
                }
              />
            ) : null
          }
          vazio={
            <EstadoVazio
              titulo="Nenhuma máquina cadastrada"
              descricao="Cadastre as máquinas da frota para apropriar custo a elas."
              acao={
                podeCadastrarMaquina ? (
                  <Link to="/equipamentos/novo" className="botao botao-secundario">
                    Cadastrar equipamento
                  </Link>
                ) : undefined
              }
            />
          }
          /* Toda lista filtrada diz quantas linhas tem (p.16 e p.18). A soma do
             rodapé é a do período inteiro, não só da página. */
          rodape={
            <RodapeTabela
              primeiro={fatia.primeiro}
              ultimo={fatia.ultimo}
              total={linhas.length}
              pagina={fatia.pagina}
              paginas={fatia.paginas}
              porPagina={porPagina}
              opcoesPorPagina={OPCOES_POR_PAGINA}
              aoMudarPagina={setPagina}
              aoMudarPorPagina={(novo) => {
                setPorPagina(novo);
                setPagina(1);
              }}
            />
          }
        />

        <ValorHoraDasFuncoes podeGravar={podeGravarValorHora} />

        {/* 0096 · o custo indireto, com o critério do rateio, e a depreciação. */}
        <CustoIndireto
          de={de}
          ate={ate}
          indireto={custos.data?.indireto ?? null}
          criterio={custos.data?.criterioDeRateio ?? null}
          podeLancar={podeLancarIndireto}
          podeEstornar={podeEstornarIndireto}
          podeConfigurar={podeConfigurar}
        />

        <DepreciacaoDasMaquinas podeGravar={podeGravarDepreciacao} />
      </div>
    </div>
  );
}

/**
 * O critério enquanto a consulta não diz qual está em uso (p.32): o título e
 * os rótulos são certos; a frase do critério, os dois controles e o botão são
 * esqueleto, na largura do formulário de verdade.
 */
function EsqueletoDoCriterio({ podeTrocar }: { readonly podeTrocar: boolean }) {
  return (
    <section className="cartao flex flex-col gap-3 p-6">
      <div className="flex flex-col gap-1">
        <h2 className="txt-secao text-tinta">Critério de custeio do combustível</h2>
        <Carregando oQue="Carregando o critério de custeio">
          <Barra className="my-1 h-3 w-3/4" />
        </Carregando>
      </div>
      {podeTrocar ? (
        <div aria-hidden="true" className="flex flex-col gap-4">
          <div className="flex flex-wrap items-start gap-4">
            <span className="flex min-w-0 flex-col gap-2">
              <span className="txt-rotulo-campo">Critério</span>
              <Barra className="h-[var(--controle-m)] w-84 max-w-full rounded-controle" />
            </span>
            <span className="flex min-w-[var(--campo-min)] flex-1 flex-col gap-2">
              <span className="txt-rotulo-campo">Motivo da troca</span>
              <Barra className="h-[var(--controle-m)] w-full rounded-controle" />
            </span>
          </div>
          <div className="flex justify-end">
            <Barra className="h-[var(--controle-m)] w-32 rounded-controle" />
          </div>
        </div>
      ) : null}
    </section>
  );
}

/** O critério da decisão 3, e a troca — com motivo, porque muda o custo de todo histórico consultado. */
function CriterioDaEmpresa({ atual, podeTrocar }: { readonly atual: Criterio; readonly podeTrocar: boolean }) {
  const base = useId();
  const cliente = useQueryClient();
  const [criterio, setCriterio] = useState<Criterio>(atual);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  /* A falha do servidor (sem permissão, rede) não é erro do campo Motivo: vai
     num bloco junto do botão, com o que fazer. Embaixo do campo, acusaria o
     campo errado. */
  const [falha, setFalha] = useState<{ readonly erro: string; readonly comoResolver: string } | null>(null);
  const trocar = useMutation({
    mutationFn: () => api.configurar(criterio, motivo.trim()),
    meta: {
      retorno: retornoDe<{ criterioCusteioCombustivel: Criterio }>((r) => ({
        titulo: 'Critério de custo do combustível trocado',
        alvo: { nome: 'Custos da empresa' },
        fatos: [`motivo: ${motivo.trim()}`],
        efeito: { rotulo: 'Critério', antes: ROTULO_DO_CRITERIO[atual], depois: ROTULO_DO_CRITERIO[r.criterioCusteioCombustivel] },
      })),
    },
    onSuccess: () => {
      setMotivo('');
      void cliente.invalidateQueries({ queryKey: ['custos'] });
    },
    onError: (motivoDaFalha) => {
      setFalha(explicarFalha(motivoDaFalha));
    },
  });

  /* O motivo e obrigatorio e se confere AO SAIR do campo (p.16, R01 p.28),
     com o erro embaixo dele; o envio confere de novo so como rede, e leva o
     foco ao campo. */
  const faltaMotivo = 'Diga por que o critério muda: a troca muda o custo de todo o histórico consultado.';

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (motivo.trim() === '') {
      setErro(faltaMotivo);
      document.getElementById(`${base}-motivo`)?.focus();
      return;
    }
    setErro(null);
    setFalha(null);
    trocar.mutate();
  }

  return (
    <section className="cartao flex flex-col gap-3 p-6">
      <div className="flex flex-col gap-1">
        <h2 className="txt-secao text-tinta">Critério de custeio do combustível</h2>
        <p className="txt-apoio text-tinta-secundaria">
          {`Em uso: ${ROTULO_DO_CRITERIO[atual]}. Critério definido pela empresa: vale para todos os lançamentos do tanque, inclusive o diesel transferido para o comboio.`}
        </p>
      </div>
      {podeTrocar ? (
        <form onSubmit={enviar} className="flex flex-col gap-4">
          <div className="flex flex-wrap items-start gap-4">
            <Campo id={`${base}-criterio`} rotulo="Critério">
              <select id={`${base}-criterio`} className="controle" value={criterio} onChange={(e) => { setCriterio(e.target.value as Criterio); }}>
                {(Object.keys(ROTULO_DO_CRITERIO) as Criterio[]).map((c) => (
                  <option key={c} value={c}>
                    {ROTULO_DO_CRITERIO[c]}
                  </option>
                ))}
              </select>
            </Campo>
            {/* Motivo é texto livre: o campo ocupa o resto da linha (R01, p.27:
                campo do tamanho do dado), e não a largura do que cabe vazio. */}
            <div className="min-w-[var(--campo-min)] flex-1">
              <Campo id={`${base}-motivo`} rotulo="Motivo da troca" erro={erro}>
                <input
                  id={`${base}-motivo`}
                  type="text"
                  autoComplete="off"
                  className="controle w-full"
                  value={motivo}
                  onChange={(e) => {
                    setErro(null);
                    setMotivo(e.target.value);
                  }}
                  onBlur={(e) => {
                    /* So cobra quando ha troca a gravar: passar pelo campo
                       sem ter mudado o criterio nao e erro. */
                    setErro(criterio !== atual && e.target.value.trim() === '' ? faltaMotivo : null);
                  }}
                  {...(erro ? { 'aria-invalid': true, 'aria-describedby': `${base}-motivo-erro` } : {})}
                />
              </Campo>
            </div>
          </div>
          {falha ? (
            <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-3">
              <span className="txt-corpo text-tinta">{falha.erro}</span>
              <span className="txt-apoio text-tinta-secundaria">{falha.comoResolver}</span>
            </div>
          ) : null}
          <div className="flex justify-end">
            <Botao nivel="secundario" type="submit" disabled={trocar.isPending || criterio === atual}>
              Trocar critério
            </Botao>
          </div>
        </form>
      ) : null}
    </section>
  );
}
